import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { visitsApi } from '@/api/visits';
import type { VisitTimePayload } from '@/api/visits';
import { tasksApi } from '@/api/tasks';

// Visit history for client
export function useVisitHistory(clientId: number | undefined) {
  return useQuery({
    queryKey: ['visitHistory', clientId],
    queryFn: () => visitsApi.getVisitHistory(clientId!, 10),
    enabled: !!clientId,
    staleTime: 60_000,
  });
}

// Client weekly visits (all statuses)
export function useClientWeeklyVisits(clientId: number | undefined) {
  return useQuery({
    queryKey: ['clientWeeklyVisits', clientId],
    queryFn: () => visitsApi.getClientWeeklyVisits(clientId!),
    enabled: !!clientId,
    staleTime: 30_000,
  });
}

// Client visits paginated (past or future)
export function useClientVisitsPaginated(
  clientId: number | undefined,
  mode: 'past' | 'future',
  enabled: boolean,
) {
  return useInfiniteQuery({
    queryKey: ['clientVisitsPaginated', clientId, mode],
    queryFn: ({ pageParam }) => visitsApi.getClientVisitsPaginated(clientId!, mode, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: enabled && !!clientId,
    staleTime: 30_000,
  });
}

// Update visit status
export function useUpdateVisit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      visitId,
      updates,
    }: {
      visitId: string;
      updates: {
        status?: 'PENDING' | 'ACTIVE' | 'VISITED' | 'SKIPPED' | 'CANCELLED';
        priority?: 'NORMAL' | 'URGENT';
        note?: string;
      };
    }) => visitsApi.updateVisit(visitId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientWeeklyVisits'] });
      queryClient.invalidateQueries({ queryKey: ['visits', 'weekly'] });
      queryClient.invalidateQueries({ queryKey: ['visits', 'active'] });
      queryClient.invalidateQueries({ queryKey: ['client-detail'] });
    },
    onError: () => {
      toast.error('Ошибка обновления визита');
    },
  });
}

export function useUpdateVisitTime() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      visitId,
      payload,
    }: {
      visitId: string;
      payload: VisitTimePayload;
    }) => visitsApi.updateVisitTime(visitId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientWeeklyVisits'] });
      queryClient.invalidateQueries({ queryKey: ['clientVisitsPaginated'] });
      queryClient.invalidateQueries({ queryKey: ['visits', 'weekly'] });
      queryClient.invalidateQueries({ queryKey: ['visitHistory'] });
      queryClient.invalidateQueries({ queryKey: ['client-detail'] });
      toast.success('Время визита сохранено');
    },
    onError: () => {
      toast.error('Не удалось сохранить время визита');
    },
  });
}

// Indoor tasks for client
export function useClientTasks(clientId: number | undefined) {
  return useQuery({
    queryKey: ['tasks', 'client', clientId],
    queryFn: () => tasksApi.getClientTasks(clientId!),
    enabled: !!clientId,
    staleTime: 30_000,
  });
}
